import { computed, ref, watch, type WatchStopHandle } from 'vue'
import { defineStore } from 'pinia'
import { getCurrentPsiAccess } from '@/api/modules/psiAccess'
import { setPsiAccessDisabledHandler } from '@/api/client'
import { ipcCall } from '@/composables/useIPC'
import {
  isDesktopRuntime,
  platformEmit,
  platformListen,
  type PlatformUnlisten,
} from '@/platform/bridge'
import {
  PSI_ACCESS_CHANGED_EVENT,
  PSI_ACCESS_DISABLED_EVENT,
  PSI_ACCESS_DISABLED_CODE,
  type PsiAccessChangedPayload,
  type PsiAccessStatus,
} from '@/constants/psiAccess'
import { useAuthStore } from '@/stores/auth'
import { IpcCommand, type EndpointProfileInfo } from '@/types/ipc'
import { env } from '@/utils/env'

export { PSI_ACCESS_CHANGED_EVENT, PSI_ACCESS_DISABLED_CODE, PSI_ACCESS_DISABLED_EVENT }
export type { PsiAccessChangedPayload, PsiAccessStatus }

const psiAccessEventSourceId = `${Date.now()}-${Math.random().toString(36).slice(2)}`

interface PsiAccessScope {
  apiBase: string
  environment: string
  companyId: number
  userId: number
}

function normalizeApiBase(value: string): string {
  return value.trim().replace(/\/+$/, '')
}

function buildEnvironment(profile: EndpointProfileInfo | null, apiBase: string): string {
  if (!profile?.present) return `default:${apiBase}`
  if (!profile.valid) return `profile-invalid:${profile.errorCode || 'unknown'}:${apiBase}`
  return [
    'profile',
    profile.companyCode?.trim().toLowerCase() || 'unknown',
    profile.channel?.trim().toLowerCase() || 'private',
    apiBase,
  ].join(':')
}

function buildProfileScope(profile: EndpointProfileInfo | null): string {
  return [
    profile?.present ? '1' : '0',
    profile?.valid ? '1' : '0',
    profile?.companyCode || '',
    profile?.apiBase || '',
    profile?.channel || '',
    profile?.errorCode || '',
  ].join('|')
}

async function resolveApiBase(): Promise<string> {
  if (!isDesktopRuntime) return normalizeApiBase(env.API_BASE_URL)
  const apiBase = await ipcCall<string>(IpcCommand.ApiBaseUrl)
  return normalizeApiBase(apiBase)
}

export const usePsiAccessStore = defineStore('psiAccess', () => {
  const authStore = useAuthStore()
  const status = ref<PsiAccessStatus>('unknown')
  const currentScope = ref<PsiAccessScope | null>(null)
  const serverEnv = ref('')
  /** Web 后台由服务端权限控制业务模块，桌面端继续检查本地 PSI 状态。 */
  const isEnabled = computed(() =>
    isDesktopRuntime ? status.value === 'enabled' : status.value !== 'disabled',
  )

  let initialized = false
  let stopAuthWatch: WatchStopHandle | null = null
  let unlistenChanged: PlatformUnlisten | null = null
  let refreshGeneration = 0
  let disabledEventDispatched = false
  let activeRefresh: { key: string; promise: Promise<PsiAccessStatus> } | null = null

  function dispatchLocalDisabled() {
    if (disabledEventDispatched || typeof window === 'undefined') return
    disabledEventDispatched = true
    window.dispatchEvent(new CustomEvent(PSI_ACCESS_DISABLED_EVENT))
  }

  function buildChangedPayload(nextStatus: PsiAccessStatus): PsiAccessChangedPayload {
    return {
      status: nextStatus,
      apiBase: currentScope.value?.apiBase || '',
      companyId: Number(authStore.userInfo?.companyId || 0),
      userId: Number(authStore.userInfo?.id || 0),
      serverEnv: serverEnv.value,
      sourceId: psiAccessEventSourceId,
    }
  }

  function broadcastStatus(nextStatus: PsiAccessStatus) {
    void platformEmit(PSI_ACCESS_CHANGED_EVENT, buildChangedPayload(nextStatus)).catch(() => {})
  }

  function applyStatus(nextStatus: PsiAccessStatus, broadcast = true) {
    status.value = nextStatus
    if (nextStatus === 'disabled') {
      dispatchLocalDisabled()
    } else {
      disabledEventDispatched = false
    }
    if (broadcast) broadcastStatus(nextStatus)
  }

  function clear(broadcast = true) {
    refreshGeneration += 1
    activeRefresh = null
    currentScope.value = null
    serverEnv.value = ''
    applyStatus('unknown', broadcast)
  }

  function handleDisabled() {
    refreshGeneration += 1
    activeRefresh = null
    applyStatus('disabled')
  }

  async function applyExternalStatus(payload: PsiAccessChangedPayload) {
    if (!['unknown', 'enabled', 'disabled'].includes(payload?.status)) return
    if (payload.sourceId === psiAccessEventSourceId) return

    const token = authStore.token
    const companyId = Number(authStore.userInfo?.companyId || 0)
    const userId = Number(authStore.userInfo?.id || 0)
    if (!token || companyId <= 0 || userId <= 0) {
      if (payload.status === 'unknown' && payload.companyId === 0 && payload.userId === 0) {
        applyStatus('unknown', false)
      }
      return
    }
    if (payload.companyId !== companyId || payload.userId !== userId || !payload.apiBase) return

    const profileScope = buildProfileScope(authStore.endpointProfileInfo)
    const generation = refreshGeneration

    let apiBase = ''
    try {
      apiBase = await resolveApiBase()
    } catch {
      if (generation === refreshGeneration) applyStatus('unknown', false)
      return
    }
    if (
      generation !== refreshGeneration ||
      authStore.token !== token ||
      Number(authStore.userInfo?.companyId || 0) !== companyId ||
      Number(authStore.userInfo?.id || 0) !== userId ||
      buildProfileScope(authStore.endpointProfileInfo) !== profileScope
    ) {
      return
    }
    if (payload.apiBase && normalizeApiBase(payload.apiBase) !== apiBase) {
      return
    }

    if (payload.status === 'unknown') {
      applyStatus('unknown', false)
      return
    }

    refreshGeneration += 1
    activeRefresh = null

    currentScope.value =
      companyId > 0 && userId > 0
        ? {
            apiBase,
            environment: buildEnvironment(authStore.endpointProfileInfo, apiBase),
            companyId,
            userId,
          }
        : null
    serverEnv.value = payload.serverEnv || ''
    applyStatus(payload.status, false)
  }

  async function doRefresh(generation: number): Promise<PsiAccessStatus> {
    const companyId = Number(authStore.userInfo?.companyId || 0)
    const userId = Number(authStore.userInfo?.id || 0)
    if (!authStore.token || companyId <= 0 || userId <= 0) {
      clear()
      return 'unknown'
    }

    currentScope.value = null
    serverEnv.value = ''
    applyStatus('unknown', false)

    try {
      const apiBase = await resolveApiBase()
      if (generation !== refreshGeneration) return status.value
      currentScope.value = {
        apiBase,
        environment: buildEnvironment(authStore.endpointProfileInfo, apiBase),
        companyId,
        userId,
      }

      const result = await getCurrentPsiAccess()
      if (generation !== refreshGeneration) return status.value
      if (
        Number(authStore.userInfo?.companyId || 0) !== companyId ||
        Number(authStore.userInfo?.id || 0) !== userId ||
        Number(result.companyId || 0) !== companyId
      ) {
        clear()
        return 'unknown'
      }

      serverEnv.value = typeof result.env === 'string' ? result.env : ''
      applyStatus(result.enabled === true ? 'enabled' : 'disabled')
      return status.value
    } catch {
      if (generation === refreshGeneration) {
        applyStatus('unknown')
      }
      return status.value
    }
  }

  function refresh(): Promise<PsiAccessStatus> {
    const profile = authStore.endpointProfileInfo
    const key = [
      authStore.token,
      Number(authStore.userInfo?.companyId || 0),
      Number(authStore.userInfo?.id || 0),
      profile?.present ? '1' : '0',
      profile?.valid ? '1' : '0',
      profile?.apiBase || '',
      profile?.channel || '',
      profile?.errorCode || '',
    ].join('|')
    if (activeRefresh?.key === key) return activeRefresh.promise

    const generation = ++refreshGeneration
    const promise = doRefresh(generation).finally(() => {
      if (activeRefresh?.promise === promise) activeRefresh = null
    })
    activeRefresh = { key, promise }
    return promise
  }

  function initialize() {
    if (initialized) return
    initialized = true
    setPsiAccessDisabledHandler(handleDisabled)
    if (!isDesktopRuntime) {
      // Web 没有本地 PSI 连接助手，菜单和路由由后台权限与接口结果控制。
      status.value = 'enabled'
      return
    }
    void platformListen<PsiAccessChangedPayload>(PSI_ACCESS_CHANGED_EVENT, (payload) => {
      void applyExternalStatus(payload)
    })
      .then((unlisten) => {
        if (!initialized) {
          unlisten()
          return
        }
        unlistenChanged = unlisten
      })
      .catch(() => {
        // 浏览器开发环境没有 Tauri event bridge，独立查询仍保持 fail-closed。
      })
    stopAuthWatch = watch(
      () => [
        authStore.token,
        authStore.userInfo?.companyId,
        authStore.userInfo?.id,
        authStore.endpointProfileInfo?.present,
        authStore.endpointProfileInfo?.valid,
        authStore.endpointProfileInfo?.apiBase,
        authStore.endpointProfileInfo?.channel,
        authStore.endpointProfileInfo?.errorCode,
      ],
      () => {
        void refresh()
      },
      { immediate: true },
    )
  }

  function dispose() {
    if (!initialized) return
    initialized = false
    stopAuthWatch?.()
    stopAuthWatch = null
    unlistenChanged?.()
    unlistenChanged = null
    setPsiAccessDisabledHandler(null)
  }

  initialize()

  return {
    status,
    isEnabled,
    refresh,
    clear,
    handleDisabled,
    initialize,
    dispose,
  }
})
